import { Virtualizer } from "@tanstack/react-virtual";
import { Job } from "~/components/build/Show/lib/types";
import { useCallback, useRef } from "react";

export function useScrollToJob(
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  jobs: Job[],
  scrollOffset: number,
) {
  const scrollIndexRef = useRef<number>(undefined);

  /**
   * Scroll element into view if it's not currently visible.
   */
  const scrollToJob = useCallback(
    (id: string) => {
      // If we've already scrolled to an index, don't scroll again
      if (scrollIndexRef.current !== undefined) {
        return;
      }

      if (!id || !rowVirtualizer.scrollRect || !rowVirtualizer.scrollElement) {
        return;
      }

      const virtualItem = rowVirtualizer.getVirtualItems().find((item) => item.key === id);
      const scrollTop = rowVirtualizer.scrollElement.scrollTop;
      const containerHeight = rowVirtualizer.scrollRect.height;
      const index = jobs.findIndex((job) => job.id === id);

      if (index === -1) {
        return;
      }

      if (
        !virtualItem ||
        virtualItem.start - scrollOffset < scrollTop ||
        virtualItem.end > scrollTop + containerHeight
      ) {
        rowVirtualizer.scrollToIndex(index, { align: "start" });
        scrollIndexRef.current = index;
      } else {
        scrollIndexRef.current = virtualItem.index;
      }
    },
    [rowVirtualizer, jobs.length, scrollOffset],
  );

  const resetScroll = useCallback(() => {
    scrollIndexRef.current = undefined;
  }, []);

  return { scrollToJob, resetScroll };
}
